import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Clock, TrendingUp, TrendingDown } from 'lucide-react'
import type { TimesheetEmployee, TimesheetDay } from '../../types/reports.types'

interface TimesheetDayDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: TimesheetEmployee | null
  dateStr: string | null
  day: TimesheetDay | null
}

const formatMinutesToHoursMinutes = (minutes: number): string => {
  if (minutes === 0) return '0 мин'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) return `${mins} мин`
  if (mins === 0) return `${hours} ч`
  return `${hours} ч ${mins} мин`
}

const formatDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-')
  const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ]
  return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`
}

export function TimesheetDayDetailsModal({
  open,
  onOpenChange,
  employee,
  dateStr,
  day,
}: TimesheetDayDetailsModalProps) {
  if (!employee || !dateStr || !day) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md relative">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Детали рабочего дня</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Employee and date info */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Сотрудник:</span>
              <span className="font-medium">{employee.employee_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Дата:</span>
              <span className="font-medium">{formatDate(dateStr)}</span>
            </div>
            {employee.department_name && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Подразделение:</span>
                <span className="font-medium">{employee.department_name}</span>
              </div>
            )}
            {employee.schedule_name && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">График:</span>
                <span className="font-medium">{employee.schedule_name}</span>
              </div>
            )}
          </div>

          <hr />

          {/* Schedule info */}
          {day.is_working_day ? (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Смена по графику</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Начало:</span>
                </div>
                <span className="font-medium">{day.scheduled_start || '-'}</span>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Конец:</span>
                </div>
                <span className="font-medium">{day.scheduled_end || '-'}</span>

                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Норма:</span>
                </div>
                <span className="font-medium">{formatMinutesToHoursMinutes(day.scheduled_minutes)}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-2">
              <span className="text-muted-foreground">Выходной день</span>
            </div>
          )}

          {day.is_working_day && (
            <>
              <hr />

              {/* Actual attendance */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Фактическое время</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Приход:</span>
                  </div>
                  <span className={`font-medium ${day.is_late ? 'text-orange-600' : ''}`}>
                    {day.check_in || 'Нет отметки'}
                  </span>

                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Уход:</span>
                  </div>
                  <span className="font-medium">{day.check_out || 'Нет отметки'}</span>

                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Отработано:</span>
                  </div>
                  <span className="font-medium">{formatMinutesToHoursMinutes(day.worked_minutes)}</span>
                </div>
              </div>

              {/* Alerts */}
              <div className="space-y-2">
                {/* Late alert */}
                {day.is_late && day.late_minutes > 0 && (
                  <Alert variant="destructive" className="bg-orange-50 border-orange-200 text-orange-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Опоздание: {formatMinutesToHoursMinutes(day.late_minutes)}
                    </AlertDescription>
                  </Alert>
                )}

                {/* No check-in alert */}
                {!day.check_in && day.scheduled_start && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Нет отметки о приходе на работу
                    </AlertDescription>
                  </Alert>
                )}

                {/* Overtime alert */}
                {day.overtime_minutes > 0 && (
                  <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                    <TrendingUp className="h-4 w-4" />
                    <AlertDescription>
                      Переработка: {formatMinutesToHoursMinutes(day.overtime_minutes)}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Undertime alert */}
                {day.worked_minutes > 0 && day.worked_minutes < day.scheduled_minutes && (
                  <Alert className="bg-yellow-50 border-yellow-200 text-yellow-800">
                    <TrendingDown className="h-4 w-4" />
                    <AlertDescription>
                      Недоработка: {formatMinutesToHoursMinutes(day.scheduled_minutes - day.worked_minutes)}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
